import type { ReactNode } from 'react';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { CARD, CARD_CLASS, CardFoot, Notice } from '@shared/ui/kit';

// Phases 27–30 (artboards 18a–18f) — the frame all six KB screens share.
//
// Every screen in the set is a header card, a tab strip, and one or more
// content cards. That was six copies of the same markup in the grey dialect,
// each drifting slightly; it is one component here so a change to the frame is
// a change to the frame, not to six screens that happen to look alike.
//
// The tab strip carries counts because the counts are the reason to switch
// tabs. A count is omitted rather than shown as zero while it is still
// loading — a tab that reads `0` and then `4` teaches the reader to distrust
// both.

export { CARD, CARD_CLASS, CardFoot } from '@shared/ui/kit';

export const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg ' +
  'bg-[var(--co-brass-ink)] px-[14px] text-[12.5px] font-bold text-[var(--co-brass-on)] ' +
  'hover:bg-[var(--co-brass-hover)] disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The one destructive control in the set — outlined, never filled.
 *
 * A filled red button is the one people press by accident, and deleting an
 * article is the action where that matters most. Same shape and height as
 * CONTROL so it sits in a row of controls without shouting.
 */
export const DANGER =
  'flex h-[34px] flex-none items-center justify-center gap-[7px] whitespace-nowrap rounded-lg ' +
  'border border-[var(--co-burgundy-line)] bg-[var(--co-burgundy-wash)] px-3 text-[12.5px] ' +
  'font-semibold text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The header card: title, one mono line of standing facts, and the actions.
 *
 * `meta` is a sentence about the whole screen rather than a row of KPI cards.
 * Three cards that mostly read zero cost ~90px of the vertical space the table
 * needs, and the same facts fit on one line here (phase 27 §6).
 */
export function KbHeader({
  title,
  meta,
  actions,
  tabs,
  lead,
}: {
  title: string;
  /** A sentence of standing facts. Chips are allowed — the editor's status
   *  belongs on this line rather than in a band of its own. */
  meta: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  /** Rendered before the title. The editor's back arrow; absent on the six
   *  tabbed screens, which are already the destination. */
  lead?: ReactNode;
}) {
  return (
    <header className={`${CARD_CLASS} flex-none px-5 py-[18px]`} style={{ ...CARD, borderRadius: 14 }}>
      {/* Row 1: title + meta line, the utility cluster in the top-right corner. */}
      <div className="flex items-center justify-between gap-[10px]">
        <div className="flex min-w-0 items-center gap-3">
          {lead}
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.08] tracking-[-.018em] text-[var(--co-ink)]">
              {title}
            </h1>
            <div className="co-mono mt-[6px] flex flex-wrap items-center gap-2 text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {meta}
            </div>
          </div>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>
      {/* Row 2: the tab strip on the left, the screen's actions on the right —
          one row, so there is no empty band between them. The child selectors
          give the shared CONTROL/PRIMARY classes the header's 10px radius
          without touching the same controls in the card bodies. */}
      {(tabs || actions) && (
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          {tabs && <div className="min-w-0 flex-1 [&>div]:mt-0">{tabs}</div>}
          {actions && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-[10px] empty:hidden [&>a]:rounded-[10px] [&>button]:rounded-[10px] [&>label]:rounded-[10px]">
              {actions}
            </div>
          )}
        </div>
      )}
    </header>
  );
}

export interface KbTab<T extends string> {
  id: T;
  label: string;
  /** Omitted while unknown — never rendered as a placeholder zero. */
  count?: number;
}

/**
 * The tab strip, plus whatever control belongs beside it.
 *
 * `aside` is where the sort control lives on the register: it names the order
 * the table is actually in, and it sits with the tabs rather than above the
 * header so it reads as a property of this view rather than of the page.
 */
export function KbTabs<T extends string>({
  tabs,
  active,
  onSelect,
  aside,
}: {
  tabs: readonly KbTab<T>[];
  active: T;
  onSelect: (id: T) => void;
  aside?: ReactNode;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-5">
      <nav
        className="flex items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1"
        aria-label="Knowledge base sections"
      >
        {tabs.map((tab) => {
          const on = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSelect(tab.id)}
              aria-current={on ? 'page' : undefined}
              className="co-mono flex items-center gap-[6px] whitespace-nowrap rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
              style={
                on
                  ? {
                      background: 'var(--co-raised)',
                      color: 'var(--co-ink)',
                      fontWeight: 600,
                      boxShadow: 'var(--co-shadow)',
                    }
                  : { color: 'var(--co-ink-3)', fontWeight: 500 }
              }
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className="font-semibold"
                  style={{ color: on ? 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      {aside}
    </div>
  );
}

/**
 * The KB screens' tone names, mapped onto the phase 36 vocabulary.
 *
 * These three were named after their colours before the tones had names. The
 * kit names them after what they MEAN, which is why `terracotta` is `warn`:
 * the colour was never the point, the "not broken yet" was.
 */
export type BandTone = 'burgundy' | 'terracotta' | 'machine';

const BAND_TONE = {
  burgundy: 'danger',
  terracotta: 'warn',
  machine: 'machine',
} as const;

/**
 * A full-width warning above the content it is about — never a cell inside it.
 *
 * A warning that scrolls away inside a grid is a warning nobody reads, so this
 * is always a sibling of the table rather than a row in it.
 */
export function WarnBand({
  tone,
  headline,
  body,
  action,
}: {
  tone: BandTone;
  headline: string;
  body: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return <Notice tone={BAND_TONE[tone]} headline={headline} body={body} action={action} />;
}
